import { useCallback } from 'react'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { clsx } from 'clsx'
import { format, addDays, addWeeks, addMonths } from 'date-fns'
import { useCalendar } from '@/hooks/useCalendar'
import { useDragDismiss } from '@/hooks/useDragDismiss'

interface DatePickerBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  value?: string
  onChange: (date: string) => void
}

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토']

const QUICK_OPTIONS = [
  { label: '오늘', resolve: () => new Date() },
  { label: '내일', resolve: () => addDays(new Date(), 1) },
  { label: '다음 주', resolve: () => addWeeks(new Date(), 1) },
  { label: '다음 달', resolve: () => addMonths(new Date(), 1) },
]

export function DatePickerBottomSheet({ isOpen, onClose, value, onChange }: DatePickerBottomSheetProps) {
  const { monthGrid, monthTitle, goToNextMonth, goToPrevMonth, selectDate, selectedDate } = useCalendar()
  const { sheetRef, dragHandlers } = useDragDismiss({ onClose })

  const handleSelect = useCallback((dateStr: string) => {
    selectDate(dateStr)
    onChange(dateStr)
    onClose()
  }, [selectDate, onChange, onClose])

  const handleQuickOption = useCallback((resolve: () => Date) => {
    const dateStr = format(resolve(), 'yyyy-MM-dd')
    handleSelect(dateStr)
  }, [handleSelect])

  const today = format(new Date(), 'yyyy-MM-dd')

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="fixed inset-x-0 bottom-0 z-50">
        <DialogPanel
          ref={sheetRef}
          className="bg-surface-primary rounded-t-2xl shadow-2xl max-h-[80dvh] overflow-y-auto pb-safe animate-in slide-in-from-bottom duration-300"
        >
          {/* Header — drag-to-dismiss (handle + title), close button stays tappable */}
          <div className="flex items-center justify-between px-4 pb-3 border-b border-base">
            <div className="flex-1 pt-3" {...dragHandlers}>
              <div className="flex justify-center pb-2">
                <div className="w-10 h-1 rounded-full bg-surface-tertiary" />
              </div>
              <h3 className="text-base font-semibold text-heading">날짜 선택</h3>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover-surface transition-colors self-end min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="닫기"
            >
              <X className="w-5 h-5 text-caption" />
            </button>
          </div>

          {/* Quick select chips */}
          <div className="flex gap-2 px-4 py-3">
            {QUICK_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                onClick={() => handleQuickOption(opt.resolve)}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-surface-tertiary text-heading hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors"
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Month navigation */}
          <div className="flex items-center justify-between px-4 py-2">
            <button onClick={goToPrevMonth} className="p-2 rounded-lg hover-surface transition-colors" aria-label="이전 달">
              <ChevronLeft className="w-5 h-5 text-body" />
            </button>
            <span className="text-sm font-semibold text-heading">{monthTitle}</span>
            <button onClick={goToNextMonth} className="p-2 rounded-lg hover-surface transition-colors" aria-label="다음 달">
              <ChevronRight className="w-5 h-5 text-body" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 px-4">
            {WEEKDAY_LABELS.map((label, i) => (
              <div
                key={label}
                className={clsx(
                  'text-center text-xs font-medium py-1',
                  i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : 'text-disabled'
                )}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-y-1 px-4 pb-4">
            {monthGrid.map((day) => {
              const isSelected = day.dateStr === (value || selectedDate)
              const isToday = day.dateStr === today
              return (
                <button
                  key={day.dateStr}
                  onClick={() => handleSelect(day.dateStr)}
                  disabled={!day.isCurrentMonth}
                  className={clsx(
                    'w-full aspect-square flex items-center justify-center rounded-full text-sm transition-colors',
                    !day.isCurrentMonth && 'text-disabled',
                    day.isCurrentMonth && !isSelected && !isToday && 'text-heading hover-surface',
                    isToday && !isSelected && 'text-primary-600 dark:text-primary-400 font-bold',
                    isSelected && 'bg-primary-500 text-white font-bold',
                  )}
                >
                  {day.day}
                </button>
              )
            })}
          </div>

          {/* Clear date option */}
          <div className="px-4 pb-4">
            <button
              onClick={() => { onChange(''); onClose() }}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-caption hover-surface transition-colors"
            >
              날짜 제거
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
